import {
  angularDifferenceDegrees,
  geographicBearingDegrees,
  readCourseBearing,
} from './courseBearing';

interface PolylineGeometryLike {
  type?: string;
  paths?: number[][][];
}

interface OrientableFeature {
  attributes?: Record<string, unknown>;
  geometry?: PolylineGeometryLike;
}

interface OrientableLayer {
  queryFeatures?: (query?: Record<string, unknown>) => Promise<{
    features?: ReadonlyArray<OrientableFeature>;
  }>;
  applyEdits?: (edits: {
    updateFeatures: OrientableFeature[];
  }) => Promise<unknown>;
}

type CimRgba = readonly [number, number, number, number];

const ORANGE_CIM_RGBA: CimRgba = [221, 161, 75, 255];

function isOrientableLayer(layer: unknown): layer is OrientableLayer {
  return typeof layer === 'object' && layer !== null;
}

function isMutableRecord(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseStrokeWidth(width: unknown): number {
  if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
    return width;
  }
  if (typeof width === 'string') {
    const parsed = Number.parseFloat(width);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return 4;
}

/**
 * Convert common color inputs to CIM RGBA [r, g, b, a] with a in 0–255.
 */
export function toCimRgba(color: unknown): CimRgba {
  if (isMutableRecord(color)) {
    const { r, g, b, a } = color;
    if (
      typeof r === 'number' &&
      typeof g === 'number' &&
      typeof b === 'number'
    ) {
      const alpha = typeof a === 'number' ? a : 1;
      return [r, g, b, Math.round(alpha * 255)];
    }
  }

  if (Array.isArray(color) && color.length >= 3) {
    const r = Number(color[0]);
    const g = Number(color[1]);
    const b = Number(color[2]);
    if (![r, g, b].every((n) => Number.isFinite(n))) {
      return ORANGE_CIM_RGBA;
    }
    if (color.length >= 4) {
      const rawAlpha = Number(color[3]);
      if (!Number.isFinite(rawAlpha)) {
        return [r, g, b, 255];
      }
      const alpha =
        rawAlpha >= 0 && rawAlpha <= 1
          ? Math.round(rawAlpha * 255)
          : Math.round(rawAlpha);
      return [r, g, b, alpha];
    }
    return [r, g, b, 255];
  }

  if (typeof color === 'string') {
    const hex = color.trim();
    if (hex.startsWith('#')) {
      const raw = hex.slice(1);
      if (raw.length === 3) {
        const r = Number.parseInt(`${raw[0]}${raw[0]}`, 16);
        const g = Number.parseInt(`${raw[1]}${raw[1]}`, 16);
        const b = Number.parseInt(`${raw[2]}${raw[2]}`, 16);
        if ([r, g, b].every((n) => Number.isFinite(n))) {
          return [r, g, b, 255];
        }
      }
      if (raw.length === 6) {
        const r = Number.parseInt(raw.slice(0, 2), 16);
        const g = Number.parseInt(raw.slice(2, 4), 16);
        const b = Number.parseInt(raw.slice(4, 6), 16);
        if ([r, g, b].every((n) => Number.isFinite(n))) {
          return [r, g, b, 255];
        }
      }
    }
    // Named CSS colors (and anything else unparsed) fall back to orange.
    return ORANGE_CIM_RGBA;
  }

  return ORANGE_CIM_RGBA;
}

export function reversePolylinePaths(paths: number[][][]): number[][][] {
  return paths.map((path) =>
    [...path].reverse().map((vertex) => [...vertex]),
  );
}

export function pathNeedsReverseForCourseBearing(
  path: number[][],
  courseBearing: number,
): boolean {
  if (path.length < 2) {
    return false;
  }

  const start = path[0];
  const end = path[path.length - 1];
  if (
    !start ||
    !end ||
    start.length < 2 ||
    end.length < 2 ||
    typeof start[0] !== 'number' ||
    typeof start[1] !== 'number' ||
    typeof end[0] !== 'number' ||
    typeof end[1] !== 'number'
  ) {
    return false;
  }

  const pathBearing = geographicBearingDegrees(
    start[0],
    start[1],
    end[0],
    end[1],
  );
  return angularDifferenceDegrees(pathBearing, courseBearing) > 90;
}

const SURVEY_TRAVEL_DIRECTION_MIN_ZOOM = 20;

export function shouldShowSurveyTravelDirectionArrows(zoom: number): boolean {
  return Number.isFinite(zoom) && zoom >= SURVEY_TRAVEL_DIRECTION_MIN_ZOOM;
}

export function withTravelDirectionArrow(
  symbol: Record<string, unknown>,
  options?: { readonly showArrows?: boolean },
): Record<string, unknown> {
  const showArrows = options?.showArrows ?? true;
  const color = symbol.color ?? symbol.currentColor;
  const cimColor = toCimRgba(color);
  const strokeWidth = parseStrokeWidth(symbol.width ?? symbol.currentWidth);
  const markerSize = Math.max(strokeWidth * 2.5, 4);

  const strokeLayer: Record<string, unknown> = {
    type: 'CIMSolidStroke',
    enable: true,
    width: strokeWidth,
    color: [...cimColor],
  };

  const symbolLayers: Record<string, unknown>[] = showArrows
    ? [
        strokeLayer,
        {
          type: 'CIMVectorMarker',
          enable: true,
          size: markerSize,
          markerPlacement: {
            type: 'CIMMarkerPlacementAlongLineSameSize',
            endings: 'WithMarkers',
            placementTemplate: [300],
            angleToLine: true,
          },
          frame: {
            xmin: -5,
            ymin: -5,
            xmax: 5,
            ymax: 5,
          },
          markerGraphics: [
            {
              type: 'CIMMarkerGraphic',
              geometry: {
                rings: [
                  [
                    [-8, -5.47],
                    [-8, 5.6],
                    [1.96, -0.03],
                    [-8, -5.47],
                  ],
                ],
              },
              symbol: {
                type: 'CIMPolygonSymbol',
                symbolLayers: [
                  {
                    type: 'CIMSolidFill',
                    enable: true,
                    color: [...cimColor],
                  },
                ],
              },
            },
          ],
        },
      ]
    : [strokeLayer];

  return {
    ...symbol,
    type: 'cim',
    color,
    currentColor: color ?? symbol.currentColor,
    width: symbol.width ?? `${strokeWidth}px`,
    currentWidth: symbol.currentWidth ?? strokeWidth,
    data: {
      type: 'CIMSymbolReference',
      symbol: {
        type: 'CIMLineSymbol',
        symbolLayers,
      },
    },
  };
}

export function buildSurveyTravelDirectionSymbol(
  symbol: Record<string, unknown>,
  zoom: number,
): Record<string, unknown> {
  return withTravelDirectionArrow(symbol, {
    showArrows: shouldShowSurveyTravelDirectionArrows(zoom),
  });
}

/**
 * Mutates CIM stroke + arrow fill colors in place when present;
 * otherwise syncs a simple-line marker color.
 */
export function applyTravelDirectionArrowColor(
  symbol: Record<string, unknown>,
  color: unknown,
): void {
  if (symbol.type === 'cim') {
    const cimColor = toCimRgba(color);
    symbol.color = color;
    symbol.currentColor = color;

    const data = symbol.data;
    if (!isMutableRecord(data)) {
      return;
    }
    const lineSymbol = data.symbol;
    if (!isMutableRecord(lineSymbol) || !Array.isArray(lineSymbol.symbolLayers)) {
      return;
    }

    for (const layer of lineSymbol.symbolLayers) {
      if (!isMutableRecord(layer)) {
        continue;
      }
      if (layer.type === 'CIMSolidStroke') {
        layer.color = [...cimColor];
        continue;
      }
      if (layer.type !== 'CIMVectorMarker' || !Array.isArray(layer.markerGraphics)) {
        continue;
      }
      for (const graphic of layer.markerGraphics) {
        if (!isMutableRecord(graphic)) {
          continue;
        }
        const graphicSymbol = graphic.symbol;
        if (
          !isMutableRecord(graphicSymbol) ||
          !Array.isArray(graphicSymbol.symbolLayers)
        ) {
          continue;
        }
        for (const fillLayer of graphicSymbol.symbolLayers) {
          if (
            isMutableRecord(fillLayer) &&
            fillLayer.type === 'CIMSolidFill'
          ) {
            fillLayer.color = [...cimColor];
          }
        }
      }
    }
    return;
  }

  if (isMutableRecord(symbol.marker)) {
    symbol.marker.color = color;
  }
}

export async function orientLayerFeaturesToCourseBearing(
  layer: unknown,
): Promise<void> {
  try {
    if (!isOrientableLayer(layer) || typeof layer.queryFeatures !== 'function') {
      return;
    }

    const result = await layer.queryFeatures({});
    const features = result?.features ?? [];
    const updateFeatures: OrientableFeature[] = [];

    for (const feature of features) {
      const geometry = feature.geometry;
      if (
        !geometry ||
        geometry.type !== 'polyline' ||
        !Array.isArray(geometry.paths) ||
        geometry.paths.length === 0
      ) {
        continue;
      }

      const courseBearing = readCourseBearing(feature.attributes);
      if (courseBearing === null) {
        continue;
      }

      const paths = geometry.paths;
      const needsReverse = paths.some((path) =>
        pathNeedsReverseForCourseBearing(path, courseBearing),
      );
      if (!needsReverse) {
        continue;
      }

      geometry.paths = paths.map((path) =>
        pathNeedsReverseForCourseBearing(path, courseBearing)
          ? reversePolylinePaths([path])[0] ?? path
          : path,
      );
      updateFeatures.push(feature);
    }

    if (
      updateFeatures.length > 0 &&
      typeof layer.applyEdits === 'function'
    ) {
      await layer.applyEdits({ updateFeatures });
    }
  } catch {
    // Swallow orientation failures so layer load still succeeds.
  }
}
